import { useState } from "react";
import FillCards from "@/components/FillCards";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FileText, Sparkles, Upload, CheckCircle2, AlertCircle, ClipboardPaste } from "lucide-react";
import { toast } from "sonner";
import JobImportPlan from "@/components/JobImportPlan";
import RegisterImportPlan from "@/components/RegisterImportPlan";

const KIND_LABELS = {
  job_registers: { label: "Job registers (workbook)", desc: "Planning, Jobs Pipeline, BPR Status and Dispatch sheets of one workbook" },
  planning:  { label: "Planning Sheet",  desc: "Job specifications (label dims, substrate, colors, die info)" },
  pipeline:  { label: "Jobs Pipeline",   desc: "Workflow status — CDR/HPDF/plates/shade card/die/material" },
  bpr:       { label: "BPR Status",      desc: "Production milestones — Printing/AVT/Flatbed/Slitting/Packing dates" },
  dispatch:  { label: "Dispatch Details",desc: "Post-dispatch — invoice, PO, boxes, rolls, vehicle" },
  sales:     { label: "Sales Invoices",  desc: "Invoice-level revenue with Party Name/Code, GSTIN, taxes" },
  purchase:  { label: "Purchase Bills",  desc: "Vendor bills with GSTIN, invoice value, taxes" },
  rmcost:    { label: "Raw Material Cost", desc: "Per-BPR material consumption and cost" },
  materials: { label: "Materials Master",desc: "Substrates, inks, adhesives, ribbons" },
  jobs:      { label: "Generic Jobs",    desc: "Job register with BPR/MPWO number" },
  employees: { label: "Employee List", desc: "Needs: Emp Id, Emp Name, Department — fills the Operators page; no logins are created" },
  production: { label: "Production Register", desc: "The Production sheet: Printing, AVT and Slitting details per BPR, added to each job's stage records. Safe to import again" },
  equipment: { label: "Equipment List", desc: "Needs: an equipment ID or code and a name (make, capacity, calibration dates optional). Word, Excel or CSV. Safe to import again" },
  stores_workbook: { label: "Stores Workbook", desc: "Item master, opening balance, GRN, issues, returns, slitting — fills the stores dashboard", stores: true },
  stores_items:    { label: "Stores · Items", desc: "Needs: Item Code, Material Name, Width (mm), UOM", stores: true },
  stores_opening:  { label: "Stores · Opening Balance", desc: "Needs: Opening Date, Slit/Shipped Roll, ERP Item Code, Opening Length (Mtr)", stores: true },
  stores_receipt:  { label: "Stores · Goods Receipt", desc: "Needs: GRN Date, GRN No, ERP ITEM CODE (or ITEM CODE + Width), Qty Received, Slit/Shipped Roll", stores: true },
  stores_issue:    { label: "Stores · Goods Issue", desc: "Needs: Date, Issue No (BPR), Department, ERP Item Code, Issue Qty, Slit/Shipped Roll", stores: true },
  stores_return:   { label: "Stores · Production Return", desc: "Needs: Date, ERP Item Code, Return Qty (Mtr), Slit/Shipped Roll", stores: true },
  stores_slitting: { label: "Stores · Slitting", desc: "Needs: Slitting Date, Parent Roll No / Item / Length, Child Roll No / Item / Length", stores: true },
  plates: { label: "Plates / Dies Register", desc: "Plate box number, teeth, cylinder and die size, matched by customer + product code and filled onto every job of that product. Safe to import again" },
  unknown:   { label: "Unknown format",  desc: "Could not detect. Try selecting kind manually below." },
  auto:      { label: "Auto-detect",     desc: "Let the system detect the kind from column headers" },
};

const KIND_OPTIONS = ["auto", "employees", "equipment", "planning", "pipeline", "bpr", "dispatch", "sales", "purchase", "rmcost", "materials",
  "stores_items", "stores_opening", "stores_receipt", "stores_issue", "stores_return", "stores_slitting"];

const ENTRY_LABELS = {
  opening: "Opening balance", receipt: "Goods receipt", issue: "Goods issue",
  return: "Production return", slit_out: "Slitting · parent", slit_in: "Slitting · child",
};

// Stores rows already in the ERP are skipped, not updated; jobs already up to date are left alone.
function importSummary(data) {
  const kind = KIND_LABELS[data.detected_kind]?.label || data.detected_kind;
  if (data.finance) {
    const c = data.finance.counts;
    return `${kind}: ${c.new.toLocaleString("en-IN")} new lines, ${c.unchanged.toLocaleString("en-IN")} already in the ERP`;
  }
  const seen = data.stores ? "already in ERP" : "updated";
  const same = (data.jobs ? `, ${data.jobs.counts.unchanged.toLocaleString("en-IN")} already up to date` : "")
    + (data.production && data.jobs ? `, ${data.production.changed.toLocaleString("en-IN")} stage records` : "");
  return `${kind}: ${data.created} new, ${data.updated} ${seen}${same}${data.total_errors ? `, ${data.total_errors} errors` : ""}`;
}

export default function CsvImport() {
  return (
    <div>
      <PageHeader subtitle="Data Migration · Daily Sync" title="Smart Import" subtitleKey="page.csv.subtitle" titleKey="page.csv.title" />
      <div className="p-8 space-y-6 max-w-6xl">
        <div className="border border-neutral-200 bg-white p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-[#FF5A00]/10 border border-[#FF5A00]/30 flex items-center justify-center">
              <Sparkles size={18} className="text-[#FF5A00]" />
            </div>
            <div>
              <h3 className="font-display font-black text-xl tracking-tight">Two ways to bring data in</h3>
              <p className="text-sm text-neutral-600 mt-1 max-w-2xl leading-relaxed">
                Either <b>upload the whole spreadsheet</b> (.xlsx/.csv) or just <b>copy rows in Excel and paste
                them here</b>. Both use the same auto-detect + merge-by-BPR/invoice logic, so re-doing is always
                safe.
              </p>
            </div>
          </div>
        </div>

        <FillCards />

        <Tabs defaultValue="paste" className="w-full">
          <TabsList className="rounded-sm bg-white border border-neutral-200">
            <TabsTrigger value="paste" className="rounded-sm" data-testid="tab-paste">
              <ClipboardPaste size={14} className="mr-1.5" />Paste from Excel
            </TabsTrigger>
            <TabsTrigger value="upload" className="rounded-sm" data-testid="tab-upload">
              <Upload size={14} className="mr-1.5" />Upload File
            </TabsTrigger>
          </TabsList>

          <TabsContent value="paste" className="mt-4">
            <PasteMode />
          </TabsContent>

          <TabsContent value="upload" className="mt-4">
            <UploadMode />
          </TabsContent>
        </Tabs>

        {/* Reference */}
        <div className="border border-neutral-200 bg-white p-5">
          <div className="label-caps mb-3">Recognised File Kinds</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(KIND_LABELS).filter(([k]) => !["unknown", "jobs", "auto"].includes(k)).map(([k, v]) => (
              <div key={k} className="border border-neutral-100 p-3 rounded-sm">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-neutral-400" />
                  <div className="font-medium text-sm">{v.label}</div>
                </div>
                <div className="text-xs text-neutral-500 mt-1 ml-6">{v.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================ PASTE MODE ============================
function PasteMode() {
  const [text, setText] = useState("");
  const [kindHint, setKindHint] = useState("auto");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const doInspect = async () => {
    if (!text.trim()) {
      toast.error("Paste some data first");
      return;
    }
    setBusy(true);
    setLastResult(null);
    try {
      const body = { text, kind_hint: kindHint === "auto" ? null : kindHint };
      const { data } = await api.post("/import/paste/inspect", body);
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doImport = async () => {
    setImporting(true);
    try {
      const body = { text, kind_hint: kindHint === "auto" ? null : kindHint };
      const { data } = await api.post("/import/paste", body);
      toast.success(importSummary(data));
      setLastResult(data);
      setPreview(null);
      setText("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="border border-neutral-200 bg-white p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="md:col-span-3">
            <label className="label-caps block mb-1.5">Paste rows here</label>
            <p className="text-xs text-neutral-500 mb-2">
              Select rows including the header row in Excel/Google Sheets → <span className="font-mono-num">Ctrl+C</span> → paste below.
            </p>
          </div>
          <div>
            <label className="label-caps block mb-1.5">Kind</label>
            <Select value={kindHint} onValueChange={setKindHint}>
              <SelectTrigger className="rounded-sm" data-testid="paste-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {KIND_OPTIONS.map(k => (
                  <SelectItem key={k} value={k}>{KIND_LABELS[k]?.label || k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Textarea
          data-testid="paste-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`BPR Number\tCustomer Name\tJob Name\tOrder Qty\tUnit\nMPWO-26270001\tHetero Labs\tPlain Label 200x100\t50000\tNOS\nMPWO-26270002\tGland Pharma\tBarcode Label\t100000\tNOS\n`}
          className="rounded-sm font-mono-num text-xs min-h-[200px] whitespace-pre"
          style={{ tabSize: 8 }}
        />

        <div className="flex justify-between items-center mt-3">
          <div className="text-xs text-neutral-500">
            {text ? `${text.split("\n").filter(l => l.trim()).length} lines pasted` : "Nothing pasted yet"}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => { setText(""); setPreview(null); }}
              disabled={!text}
              className="rounded-sm"
            >Clear</Button>
            <Button
              data-testid="paste-inspect-btn"
              onClick={doInspect}
              disabled={!text.trim() || busy}
              className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]"
            >
              {busy ? "Analysing…" : "Preview →"}
            </Button>
          </div>
        </div>
      </div>

      {preview && <PreviewCard preview={preview} onImport={doImport} onCancel={() => setPreview(null)} importing={importing} />}
      {lastResult && <ResultCard result={lastResult} />}
    </div>
  );
}

// ============================ UPLOAD MODE ============================
function UploadMode() {
  const [inspecting, setInspecting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pending, setPending] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const inspect = async (file) => {
    if (!file) return;
    setInspecting(true);
    setLastResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/inspect", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPending({ file, inspection: data });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setInspecting(false); }
  };

  const doImport = async (resync = false) => {
    if (!pending) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", pending.file);
      const { data } = await api.post(`/import/auto${resync === true ? "?resync=true" : ""}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(importSummary(data));
      setLastResult(data);
      setPending(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="border border-neutral-200 bg-white p-6">
        <label className="label-caps block mb-2">Attach .xlsx, .csv or a Word equipment list (.docx)</label>
        <input
          type="file"
          accept=".csv,.xlsx,.xlsm,.docx"
          onChange={(e) => {
            inspect(e.target.files?.[0]);
            e.target.value = ""; // picking the same file again (a re-import) must preview again
          }}
          disabled={inspecting || importing}
          className="text-sm text-neutral-700 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-[#FF5A00] file:text-white file:font-medium hover:file:bg-[#e04f00] file:cursor-pointer disabled:opacity-50"
          data-testid="smart-import-input"
        />
        {inspecting && <div className="mt-3 text-xs text-neutral-500 animate-pulse">Reading file & detecting format…</div>}
      </div>
      {pending && (
        <PreviewCard
          preview={pending.inspection}
          onImport={doImport}
          onCancel={() => setPending(null)}
          importing={importing}
        />
      )}
      {lastResult && <ResultCard result={lastResult} />}
    </div>
  );
}

// ============================ Shared Preview ============================
function PreviewCard({ preview, onImport, onCancel, importing }) {
  const kind = preview.detected_kind;
  if (KIND_LABELS[kind]?.stores) {
    return <StoresPreview preview={preview} onImport={onImport} onCancel={onCancel} importing={importing} />;
  }
  if (preview.jobs) {
    return <JobsPreview preview={preview} onImport={onImport} onCancel={onCancel} importing={importing} />;
  }
  if (preview.finance) {
    return <RegisterPreview preview={preview} onImport={onImport} onCancel={onCancel} importing={importing} />;
  }
  if (preview.employees) {
    return <EmployeesPreview preview={preview} onImport={onImport} onCancel={onCancel} importing={importing} />;
  }
  if (preview.production) {
    return <ProductionPreview preview={preview} onImport={onImport} onCancel={onCancel} importing={importing} />;
  }
  if (preview.equipment) {
    return <EquipmentPreview preview={preview} onImport={onImport} onCancel={onCancel} importing={importing} />;
  }
  if (preview.plates) {
    return <PlatesPreview preview={preview} onImport={onImport} onCancel={onCancel} importing={importing} />;
  }
  return (
    <div className="border border-neutral-200 bg-white" data-testid="import-preview">
      <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CheckCircle2 size={18} className="text-emerald-600" />
          <div>
            <div className="label-caps">Detected · {kind}</div>
            <div className="font-display font-bold text-lg tracking-tight mt-0.5">
              {KIND_LABELS[kind]?.label || kind}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono-num text-2xl font-black">{preview.row_count.toLocaleString("en-IN")}</div>
          <div className="text-xs text-neutral-500">rows detected</div>
        </div>
      </div>

      <div className="p-5 border-b border-neutral-200 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Columns matched" value={`${preview.columns_matched} / ${preview.columns_total}`} />
        <Stat label="Rows" value={preview.row_count.toLocaleString("en-IN")} />
        <Stat label="Match rate" value={`${Math.round((preview.columns_matched / Math.max(1, preview.columns_total)) * 100)}%`} />
        <Stat label="Merge key" value={kind === "materials" ? "SKU" : "BPR/MPWO"} />
      </div>

      <div className="p-5">
        <div className="label-caps mb-3">Column Mapping</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 text-sm max-h-96 overflow-y-auto pr-2">
          {preview.headers.map((h) => {
            const target = preview.column_mapping[h];
            return (
              <div key={h} className="flex items-center justify-between border border-neutral-100 px-3 py-1.5 rounded-sm">
                <span className="font-mono-num text-xs text-neutral-700 truncate max-w-[45%]" title={h}>{h || "—"}</span>
                <span className="text-xs text-neutral-400">→</span>
                <span className={`text-xs font-medium truncate max-w-[45%] ${target ? "text-emerald-700" : "text-neutral-400 italic"}`} title={target || "ignored"}>
                  {target || "(ignored)"}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-5 py-4 border-t border-neutral-200 bg-neutral-50 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} className="rounded-sm" disabled={importing}>Cancel</Button>
        <Button
          data-testid="confirm-import"
          onClick={onImport}
          disabled={importing || kind === "unknown"}
          className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]"
        >
          <Upload size={14} className="mr-1.5" />
          {importing ? "Importing…" : `Import ${preview.row_count.toLocaleString("en-IN")} rows`}
        </Button>
      </div>
    </div>
  );
}

// Job registers (Planning / Jobs Pipeline / BPR Status / Dispatch): what the import will do, row by row.
const STAGE_LABEL = { stores_issue: "Stores issue", printing: "Printing", avt: "AVT", slitting: "Slitting & Numbering", quality: "Quality" };

// The stage-record sheets of a workbook (Production, Material Issue, Quality): what each fills in, and what it changes.
function ProductionSection({ p, done = false }) {
  return (
    <div data-testid="production-section">
      <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50 label-caps">Stage records{done ? "" : " from the same file"}</div>
      <div className="px-5 py-4 border-b border-neutral-200">
        <table className="text-xs w-full" data-testid="production-sheets">
          <thead className="label-caps text-left"><tr><th className="py-1 pr-4">Sheet</th><th className="pr-4">Goes into</th><th className="pr-4 text-right">BPR rows</th><th className="text-right">Stage records</th></tr></thead>
          <tbody>
            {p.sheets.map((s) => (
              <tr key={s.sheet} className="border-t border-neutral-100"><td className="py-1 pr-4 font-medium">{s.sheet}</td><td className="pr-4 text-neutral-600">{s.label}</td><td className="pr-4 text-right font-mono-num">{s.rows.toLocaleString("en-IN")}</td><td className="text-right font-mono-num">{s.records.toLocaleString("en-IN")}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-4 border-b border-neutral-200" data-testid="production-counts">
        <Stat label="Jobs found in the ERP" value={p.found.toLocaleString("en-IN")} accent="text-emerald-700" />
        <Stat label={p.with_this_file ? "Not in the ERP yet" : "Not in the ERP"} value={(p.not_in_erp + (p.with_this_file || 0)).toLocaleString("en-IN")} accent={p.not_in_erp ? "text-rose-700" : ""} />
        <Stat label={done ? "Stage records updated" : "Stage records to update"} value={p.changed.toLocaleString("en-IN")} accent="text-blue-700" />
        <Stat label="Already as they are" value={p.unchanged.toLocaleString("en-IN")} />
      </div>
      <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="label-caps mb-2">By stage</div>
          <div className="text-xs space-y-1">
            {Object.entries(p.by_stage).map(([k, v]) => (
              <div key={k} className="flex gap-3"><span className="w-40">{STAGE_LABEL[k] || k}</span><span className="text-blue-700">{v.changed.toLocaleString("en-IN")} {done ? "updated" : "to update"}</span><span className="text-neutral-500">{v.unchanged.toLocaleString("en-IN")} as they are</span></div>
            ))}
          </div>
          <div className="text-[11px] text-neutral-500 mt-2">Only the stage details are filled in. A job's status and which stage is open do not change, and anything already typed in the app is kept.</div>
        </div>
        <div>
          <div className="label-caps mb-2">Not in the ERP{p.not_in_erp ? ` (${p.not_in_erp})` : ""}</div>
          <div className="text-xs font-mono-num text-neutral-600 break-words">{p.not_in_erp_sample.join(", ") || "—"}{p.not_in_erp > p.not_in_erp_sample.length ? " …" : ""}</div>
          <div className="text-[11px] text-neutral-500 mt-2">{p.with_this_file ? `${p.with_this_file.toLocaleString("en-IN")} more are brought in by this file's job sheets and get their stage records too. ` : ""}This never creates a job on its own.</div>
        </div>
      </div>
    </div>
  );
}

function ProductionPreview({ preview, onImport, onCancel, importing }) {
  const p = preview.production;
  const todo = p.changed;
  return (
    <div className="border border-neutral-200 bg-white" data-testid="import-preview">
      <div className="px-5 py-4 border-b border-neutral-200 flex items-center gap-3">
        <CheckCircle2 size={18} className="text-emerald-600" />
        <div>
          <div className="label-caps">Detected · stage records</div>
          <div className="font-display font-bold text-lg tracking-tight mt-0.5">{p.bprs.toLocaleString("en-IN")} BPRs with production details</div>
        </div>
      </div>
      <ProductionSection p={p} />
      <div className="px-5 py-4 border-t border-neutral-200 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} className="rounded-sm">Cancel</Button>
        <Button onClick={onImport} disabled={importing || todo === 0} data-testid="import-confirm" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          {importing ? "Importing…" : todo === 0 ? "Nothing new to import" : `Update ${todo.toLocaleString("en-IN")} stage records`}
        </Button>
      </div>
    </div>
  );
}

// Plate / die register: matched by customer + product code (not a BPR), so it's its own shape rather than
// the generic column-mapping preview — see backend/routes/plates_import.py.
function PlatesPreview({ preview, onImport, onCancel, importing }) {
  const p = preview.plates;
  const nothing = p.jobs_matched === 0;
  return (
    <div className="border border-neutral-200 bg-white" data-testid="import-preview">
      <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <CheckCircle2 size={18} className="text-emerald-600" />
          <div>
            <div className="label-caps">Detected</div>
            <div className="font-display font-bold text-lg tracking-tight mt-0.5">{KIND_LABELS.plates.label}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono-num text-2xl font-black">{p.jobs_matched.toLocaleString("en-IN")}</div>
          <div className="text-xs text-neutral-500">jobs to fill, of {p.rows_read.toLocaleString("en-IN")} rows read</div>
        </div>
      </div>
      <div className="p-5 grid grid-cols-2 md:grid-cols-3 gap-4">
        <Stat label="Fields to fill" value={p.fields_to_fill.toLocaleString("en-IN")} accent="text-emerald-700" />
        <Stat label="Jobs to update" value={p.jobs_matched.toLocaleString("en-IN")} />
        <Stat label="Rows with no matching job" value={p.rows_with_no_matching_job.toLocaleString("en-IN")} />
      </div>
      {p.sample?.length > 0 && (
        <div className="px-5 pb-5">
          <div className="label-caps mb-2">Sample</div>
          <div className="space-y-1 text-xs max-h-56 overflow-y-auto">
            {p.sample.map((s, i) => (
              <div key={i} className="border border-neutral-100 px-3 py-1.5 rounded-sm flex items-center justify-between gap-2">
                <span className="font-mono-num text-neutral-700">{s.bpr}</span>
                <span className="text-neutral-500 truncate">{Object.entries(s.fields).map(([k, v]) => `${k}: ${v}`).join(" · ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="px-5 py-4 border-t border-neutral-200 bg-neutral-50 flex flex-wrap items-center justify-end gap-2">
        {nothing && <span className="mr-auto text-sm text-neutral-600" data-testid="nothing-new">Nothing to fill: every matched job already has these details.</span>}
        <Button variant="outline" onClick={onCancel} className="rounded-sm" disabled={importing}>Cancel</Button>
        <Button data-testid="confirm-import" onClick={onImport} disabled={importing || nothing} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          <Upload size={14} className="mr-1.5" />
          {importing ? "Importing…" : `Fill ${p.jobs_matched.toLocaleString("en-IN")} jobs`}
        </Button>
      </div>
    </div>
  );
}

function EquipmentPreview({ preview, onImport, onCancel, importing }) {
  const e = preview.equipment, c = e.counts;
  return (
    <div className="border border-neutral-200 bg-white" data-testid="import-preview">
      <div className="px-5 py-4 border-b border-neutral-200 flex items-center gap-3">
        <CheckCircle2 size={18} className="text-emerald-600" />
        <div>
          <div className="label-caps">Detected · equipment list</div>
          <div className="font-display font-bold text-lg tracking-tight mt-0.5">{e.total.toLocaleString("en-IN")} equipment</div>
        </div>
      </div>
      <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-4 border-b border-neutral-200" data-testid="equipment-counts">
        <Stat label="New" value={c.create.toLocaleString("en-IN")} accent="text-emerald-700" />
        <Stat label="Updated" value={c.update.toLocaleString("en-IN")} accent="text-blue-700" />
        <Stat label="Already as they are" value={c.unchanged.toLocaleString("en-IN")} />
        <Stat label="Skipped" value={c.skipped.toLocaleString("en-IN")} accent={c.skipped ? "text-rose-700" : ""} />
      </div>
      <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-6 border-b border-neutral-200">
        <div>
          <div className="label-caps mb-2">Types</div>
          <div className="flex flex-wrap gap-1.5">
            {e.types.map((t) => <span key={t.name} className="text-xs px-2 py-1 rounded-sm border border-neutral-200 text-neutral-700">{t.name} · {t.count}</span>)}
          </div>
          <div className="text-[11px] text-neutral-500 mt-2">
            Importing the same list again changes nothing. Blank cells never overwrite what is on the Equipment page
            {c.kept > 0 ? `, and ${c.kept.toLocaleString("en-IN")} value${c.kept === 1 ? "" : "s"} changed there since the last import ${c.kept === 1 ? "is" : "are"} kept` : ""}.
          </div>
        </div>
        <div>
          <div className="label-caps mb-2">First rows</div>
          <div className="text-xs space-y-1">
            {e.sample.map((p, i) => <div key={i} className="flex gap-3"><span className="font-mono-num w-24 shrink-0">{p.code || "—"}</span><span className="flex-1 truncate">{p.name}</span><span className="text-neutral-500">{p.equip_type}</span></div>)}
          </div>
        </div>
      </div>
      {e.skipped.length > 0 && (
        <div className="px-5 py-3 border-b border-neutral-200 text-xs text-rose-700">
          {e.skipped.map((s) => <div key={s.row}>Row {s.row}: {s.reason}</div>)}
        </div>
      )}
      <div className="px-5 py-4 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} className="rounded-sm">Cancel</Button>
        <Button onClick={onImport} disabled={importing || (c.create + c.update === 0)} data-testid="import-confirm" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          {importing ? "Importing…" : c.create + c.update === 0 ? "Nothing new to import" : `Import ${(c.create + c.update).toLocaleString("en-IN")} equipment`}
        </Button>
      </div>
    </div>
  );
}

function EmployeesPreview({ preview, onImport, onCancel, importing }) {
  const e = preview.employees, c = e.counts;
  return (
    <div className="border border-neutral-200 bg-white" data-testid="import-preview">
      <div className="px-5 py-4 border-b border-neutral-200 flex items-center gap-3">
        <CheckCircle2 size={18} className="text-emerald-600" />
        <div>
          <div className="label-caps">Detected · employee list</div>
          <div className="font-display font-bold text-lg tracking-tight mt-0.5">{e.people.toLocaleString("en-IN")} people, {e.floor.toLocaleString("en-IN")} on the shop floor</div>
        </div>
      </div>
      <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-4 border-b border-neutral-200" data-testid="employees-counts">
        <Stat label="New" value={c.create.toLocaleString("en-IN")} accent="text-emerald-700" />
        <Stat label="Updated" value={c.update.toLocaleString("en-IN")} accent="text-blue-700" />
        <Stat label="Already as they are" value={c.unchanged.toLocaleString("en-IN")} />
        <Stat label="Skipped" value={c.skipped.toLocaleString("en-IN")} accent={c.skipped ? "text-rose-700" : ""} />
      </div>
      <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-6 border-b border-neutral-200">
        <div>
          <div className="label-caps mb-2">Departments</div>
          <div className="flex flex-wrap gap-1.5">
            {e.departments.map((d) => (
              <span key={d.name} className={`text-xs px-2 py-1 rounded-sm border ${d.production ? "border-orange-300 bg-orange-50 text-orange-900" : "border-neutral-200 text-neutral-600"}`}>
                {d.name} · {d.count}
              </span>
            ))}
          </div>
          <div className="text-[11px] text-neutral-500 mt-2">Orange: shop-floor departments, offered on the production sheets. The others are kept as office / support. No logins are created.</div>
        </div>
        <div>
          <div className="label-caps mb-2">First rows</div>
          <div className="text-xs space-y-1">
            {e.sample.map((p) => <div key={p.code} className="flex gap-3"><span className="font-mono-num w-16">{p.code}</span><span className="flex-1">{p.name}</span><span className="text-neutral-500">{p.department}</span></div>)}
          </div>
        </div>
      </div>
      {e.skipped.length > 0 && (
        <div className="px-5 py-3 border-b border-neutral-200 text-xs text-rose-700">
          {e.skipped.map((s) => <div key={s.row}>Row {s.row}: {s.reason}</div>)}
        </div>
      )}
      <div className="px-5 py-4 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} className="rounded-sm">Cancel</Button>
        <Button onClick={onImport} disabled={importing || (c.create + c.update === 0)} data-testid="import-confirm" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          {importing ? "Importing…" : `Import ${(c.create + c.update).toLocaleString("en-IN")} people`}
        </Button>
      </div>
    </div>
  );
}

function JobsPreview({ preview, onImport, onCancel, importing }) {
  const c = preview.jobs.counts;
  const stages = preview.production ? preview.production.changed : 0;
  const fillable = c.fillable || 0;
  const nothing = c.new + c.updated + stages + fillable + (c.merged || 0) + (c.left_pipeline || 0) === 0;
  return (
    <div className="border border-neutral-200 bg-white" data-testid="import-preview">
      <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <CheckCircle2 size={18} className="text-emerald-600" />
          <div>
            <div className="label-caps">Detected · job registers</div>
            <div className="font-display font-bold text-lg tracking-tight mt-0.5">
              {KIND_LABELS[preview.detected_kind]?.label || preview.detected_kind}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono-num text-2xl font-black">{preview.jobs.bprs.toLocaleString("en-IN")}</div>
          <div className="text-xs text-neutral-500">BPRs in {preview.row_count.toLocaleString("en-IN")} rows</div>
        </div>
      </div>
      <JobImportPlan plan={preview.jobs} readElsewhere={(preview.production?.sheets || []).map((s) => s.sheet)} />
      {preview.production && <ProductionSection p={preview.production} />}
      <div className="px-5 py-4 border-t border-neutral-200 bg-neutral-50 flex flex-wrap items-center justify-end gap-2">
        {nothing && <span className="mr-auto text-sm text-neutral-600" data-testid="nothing-new">Nothing new: every job in this file is already up to date in the ERP.</span>}
        <Button variant="outline" onClick={onCancel} className="rounded-sm" disabled={importing}>Cancel</Button>
        <Button data-testid="confirm-import" onClick={onImport} disabled={importing || nothing} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          <Upload size={14} className="mr-1.5" />
          {importing ? "Importing…" : `Import · ${c.new.toLocaleString("en-IN")} new, ${c.updated.toLocaleString("en-IN")} updated${preview.production ? `, ${stages.toLocaleString("en-IN")} stage records` : ""}${fillable ? `, ${fillable.toLocaleString("en-IN")} spec-filled` : ""}`}
        </Button>
      </div>
    </div>
  );
}

// Sales / purchase / RM cost registers: new lines are added, lines already in the ERP are left as they are.
function RegisterPreview({ preview, onImport, onCancel, importing }) {
  const plan = preview.finance;
  const c = plan.counts;
  return (
    <div className="border border-neutral-200 bg-white" data-testid="import-preview">
      <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <CheckCircle2 size={18} className="text-emerald-600" />
          <div>
            <div className="label-caps">Detected · {plan.label}</div>
            <div className="font-display font-bold text-lg tracking-tight mt-0.5">{KIND_LABELS[plan.kind]?.label || plan.kind}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono-num text-2xl font-black">{c.lines.toLocaleString("en-IN")}</div>
          <div className="text-xs text-neutral-500">lines in {preview.row_count.toLocaleString("en-IN")} rows</div>
        </div>
      </div>
      <RegisterImportPlan plan={plan} />
      <div className="px-5 py-4 border-t border-neutral-200 bg-neutral-50 flex flex-wrap items-center justify-end gap-2">
        {c.new === 0 && <span className="mr-auto text-sm text-neutral-600" data-testid="nothing-new">Nothing new: every line in this file is already in the ERP.</span>}
        <Button variant="outline" onClick={onCancel} className="rounded-sm" disabled={importing}>Cancel</Button>
        <Button data-testid="confirm-import" onClick={onImport} disabled={importing || c.new === 0} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          <Upload size={14} className="mr-1.5" />
          {importing ? "Importing…" : `Import · ${c.new.toLocaleString("en-IN")} new lines`}
        </Button>
      </div>
    </div>
  );
}

function StoresPreview({ preview, onImport, onCancel, importing }) {
  const kind = preview.detected_kind;
  const st = preview.stores;
  const blocked = preview.required_missing.length > 0;
  const plan = st?.plan;
  const stale = preview.can_resync && plan ? plan.to_remove || 0 : 0;
  const [resync, setResync] = useState(false);
  const nothingNew = plan && st.items === 0 && Object.keys(plan.new_entries).length === 0 && !(resync && stale);
  return (
    <div className="border border-neutral-200 bg-white" data-testid="import-preview">
      <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {blocked ? <AlertCircle size={18} className="text-rose-600" /> : <CheckCircle2 size={18} className="text-emerald-600" />}
          <div>
            <div className="label-caps">Detected · stores</div>
            <div className="font-display font-bold text-lg tracking-tight mt-0.5">{KIND_LABELS[kind].label}</div>
          </div>
        </div>
        {!blocked && (
          <div className="text-right">
            <div className="font-mono-num text-2xl font-black">{preview.row_count.toLocaleString("en-IN")}</div>
            <div className="text-xs text-neutral-500">rows in file</div>
          </div>
        )}
      </div>

      <div className="p-5 border-b border-neutral-200">
        <div className="label-caps mb-3">Required columns</div>
        <div className="space-y-1.5">
          {preview.sheets.map((sh) => (
            <div key={sh.sheet} className="flex items-start justify-between gap-3 border border-neutral-100 px-3 py-2 rounded-sm text-sm" data-testid={`required-${sh.sheet}`}>
              <span className="font-medium">{sh.sheet}</span>
              {sh.missing.length === 0
                ? <span className="text-xs text-emerald-700">All present</span>
                : <span className="text-xs text-rose-700 text-right">Missing: {sh.missing.join(", ")}</span>}
            </div>
          ))}
        </div>
        {blocked && (
          <p className="text-xs text-rose-700 mt-2">
            Add the missing columns (headers as shown) and try again. Names, widths, conversions and values are filled in automatically.
          </p>
        )}
      </div>

      {st && (
        <div className="p-5 border-b border-neutral-200 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <div className="label-caps mb-2">Will be imported</div>
            <table className="w-full text-sm">
              <tbody>
                {st.items > 0 && (
                  <tr className="border-t border-neutral-100">
                    <td className="py-1.5">
                      Items <span className="text-xs text-neutral-500">· {plan.items_new} new, {plan.items_known} updated</span>
                    </td>
                    <td className="py-1.5 text-right font-mono-num">{st.items.toLocaleString("en-IN")}</td>
                  </tr>
                )}
                {Object.entries(plan.new_entries).map(([t, n]) => (
                  <tr key={t} className="border-t border-neutral-100">
                    <td className="py-1.5">{ENTRY_LABELS[t] || t}</td>
                    <td className="py-1.5 text-right font-mono-num">{n.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
                {st.rolls > 0 && (
                  <tr className="border-t border-neutral-100 text-neutral-500">
                    <td className="py-1.5">New rolls</td>
                    <td className="py-1.5 text-right font-mono-num">{st.rolls.toLocaleString("en-IN")}</td>
                  </tr>
                )}
                {plan.already_in_erp > 0 && (
                  <tr className="border-t border-neutral-100 text-neutral-500" data-testid="already-in-erp">
                    <td className="py-1.5">Already in the ERP · skipped</td>
                    <td className="py-1.5 text-right font-mono-num">{plan.already_in_erp.toLocaleString("en-IN")}</td>
                  </tr>
                )}
              </tbody>
            </table>
            {nothingNew && <div className="mt-2 text-sm text-neutral-600">Nothing new: every row in this file is already in the ERP.</div>}
            {stale > 0 && (
              <label className="mt-3 flex items-start gap-2 border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 cursor-pointer" data-testid="stores-resync">
                <input type="checkbox" checked={resync} onChange={(e) => setResync(e.target.checked)} className="mt-0.5 accent-[#FF5A00]" data-testid="stores-resync-check" />
                <span>
                  <b>{stale.toLocaleString("en-IN")}</b> row{stale === 1 ? "" : "s"} brought in by an earlier import of this workbook {stale === 1 ? "has" : "have"} since
                  been corrected or deleted in it. Tick to replace them with the file's rows; otherwise the ERP keeps the old
                  and the corrected version, and that stock is counted twice. Movements entered in the app are never touched.
                </span>
              </label>
            )}
            {st.reconciliation.items_checked > 0 && (
              <div className="mt-3 text-xs text-neutral-600">
                Stock report check: <b className="font-mono-num">{st.reconciliation.items_matching}/{st.reconciliation.items_checked}</b> items match the sheet
                {st.reconciliation.rolls_checked > 0 && (
                  <> · lot-wise <b className="font-mono-num">{st.reconciliation.rolls_matching}/{st.reconciliation.rolls_checked}</b> rolls</>
                )}
              </div>
            )}
          </div>
          <div>
            <div className="label-caps mb-2">Exceptions · {st.exception_total}</div>
            {st.exception_counts.length === 0 && <div className="text-sm text-emerald-700">None — every row imports.</div>}
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {st.exception_counts.map((x) => (
                <div key={x.sheet + x.issue} className="flex gap-2 text-xs">
                  <span className="font-mono-num w-10 text-right shrink-0">{x.count}</span>
                  <span className={"uppercase tracking-widest text-[10px] w-16 shrink-0 " +
                    (x.severity === "error" ? "text-rose-700" : x.severity === "warning" ? "text-amber-700" : "text-neutral-500")}>
                    {x.severity}
                  </span>
                  <span className="text-neutral-700">{x.sheet}: {x.issue}</span>
                </div>
              ))}
            </div>
            {st.exception_counts.length > 0 && (
              <p className="text-[11px] text-neutral-500 mt-2">
                error = row skipped · warning = counted for the item, not a roll · check = sheet disagrees with its rows
              </p>
            )}
          </div>
        </div>
      )}

      <div className="px-5 py-4 bg-neutral-50 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} className="rounded-sm" disabled={importing}>Cancel</Button>
        <Button
          data-testid="confirm-import"
          onClick={() => onImport(resync && stale > 0)}
          disabled={importing || blocked || !st || nothingNew}
          className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]"
        >
          <Upload size={14} className="mr-1.5" />
          {importing ? "Importing…" : resync && stale > 0 ? "Import and replace corrected rows" : "Import to stores"}
        </Button>
      </div>
    </div>
  );
}

function ResultCard({ result }) {
  if (result.jobs) {
    return (
      <div className="border border-emerald-200 bg-white" data-testid="import-result">
        <div className="px-5 py-4 border-b border-emerald-200 bg-emerald-50/40 flex items-center gap-2">
          <CheckCircle2 size={18} className="text-emerald-700" />
          <div className="font-display font-bold text-lg">Import complete</div>
          <span className="text-xs text-emerald-900 ml-2">Jobs, dashboards and the stores queue now show this file.</span>
        </div>
        <JobImportPlan plan={result.jobs} done readElsewhere={(result.production?.sheets || []).map((s) => s.sheet)} />
        {result.production && <ProductionSection p={result.production} done />}
      </div>
    );
  }
  if (result.production) {
    return (
      <div className="border border-emerald-200 bg-white" data-testid="import-result">
        <div className="px-5 py-4 border-b border-emerald-200 bg-emerald-50/40 flex items-center gap-2">
          <CheckCircle2 size={18} className="text-emerald-700" />
          <div className="font-display font-bold text-lg">Import complete</div>
          <span className="text-xs text-emerald-900 ml-2">Each job's Production Flow now shows these stage details.</span>
        </div>
        <ProductionSection p={result.production} done />
      </div>
    );
  }
  if (result.finance) {
    return (
      <div className="border border-emerald-200 bg-white" data-testid="import-result">
        <div className="px-5 py-4 border-b border-emerald-200 bg-emerald-50/40 flex items-center gap-2">
          <CheckCircle2 size={18} className="text-emerald-700" />
          <div className="font-display font-bold text-lg">Import complete</div>
          <span className="text-xs text-emerald-900 ml-2">The dashboard, Sales / Purchases pages and job costing now include these lines.</span>
        </div>
        <RegisterImportPlan plan={result.finance} done />
      </div>
    );
  }
  return (
    <div className="border border-emerald-200 bg-emerald-50/30 p-5" data-testid="import-result">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle2 size={18} className="text-emerald-700" />
        <div className="font-display font-bold text-lg">Import complete</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Kind" value={KIND_LABELS[result.detected_kind]?.label || result.detected_kind} />
        <Stat label="New" value={result.created.toLocaleString("en-IN")} accent="text-emerald-700" />
        <Stat label={result.stores ? "Already in ERP" : "Updated"} value={result.updated.toLocaleString("en-IN")} accent="text-blue-700" />
        <Stat label="Errors" value={result.total_errors} accent={result.total_errors ? "text-rose-700" : ""} />
      </div>
      {result.stores && (
        <p className="text-xs text-emerald-900 mt-3">
          Stores updated: {result.stores.rolls.toLocaleString("en-IN")} rolls, {result.stores.items} items{result.stores.removed ? `, ${result.stores.removed.toLocaleString("en-IN")} superseded rows replaced` : ""}. The stores
          dashboard, stock and job queue now include this file.
        </p>
      )}
      {result.errors?.length > 0 && (
        <div className="mt-4 border-t border-emerald-200 pt-3">
          <div className="label-caps text-rose-700 mb-2 flex items-center gap-1.5">
            <AlertCircle size={12} />First few errors
          </div>
          <div className="text-xs font-mono-num space-y-1 max-h-40 overflow-y-auto">
            {result.errors.slice(0, 10).map((e, i) => (
              <div key={i}>Row {e.row}: {e.error}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="label-caps">{label}</div>
      <div className={`font-mono-num text-xl font-bold mt-1 ${accent || "text-neutral-900"}`}>{value}</div>
    </div>
  );
}
